// Trip Budget (PERSONAL_NOTES.md#trip-budget): the budget a user sets for a
// store visit, compared live against the basket total. Remaining is always
// derived from the total, never stored, so it can't drift from it.

/** Spent / budget at which the budget turns orange. */
export const BUDGET_WARN_RATIO = 0.8;

export type BudgetStatus = 'ok' | 'warn' | 'over';

export interface BudgetState {
  budget: number;
  spent: number;
  /** budget - spent; negative when over. */
  remaining: number;
  /** spent / budget (can exceed 1). */
  ratio: number;
  status: BudgetStatus;
}

export const getBudgetState = (budget: number | null | undefined, spent: number): BudgetState | null => {
  if (!budget || budget <= 0) return null;
  const ratio = spent / budget;
  const status: BudgetStatus = spent > budget + 0.004 ? 'over' : ratio >= BUDGET_WARN_RATIO ? 'warn' : 'ok';
  return { budget, spent, remaining: Math.round((budget - spent) * 100) / 100, ratio, status };
};

/** Tailwind classes per status: green inside the budget, orange near it, red over it. */
export const BUDGET_TONE: Record<BudgetStatus, { text: string; bar: string; soft: string }> = {
  ok: { text: 'text-emerald-500', bar: 'bg-emerald-500', soft: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  warn: { text: 'text-orange-500', bar: 'bg-orange-500', soft: 'bg-orange-50 text-orange-600 border-orange-100' },
  over: { text: 'text-rose-500', bar: 'bg-rose-500', soft: 'bg-rose-50 text-rose-600 border-rose-100' },
};

/** Parses a typed amount ("45", "45.5", "45,50"); null when it isn't a positive number. */
export const parseBudgetInput = (raw: string): number | null => {
  const n = Number(raw.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
};
